import { useState } from "react"
import { Link } from "react-router-dom"
import { ClientAvatar } from "@/components/client-avatar"
import { Button } from "@/components/ui/button"
import { EyeIcon, TruckIcon, UserIcon, CalendarIcon, ArrowRightIcon, CopyIcon } from "lucide-react"
import { cn } from "@/lib/utils"

// ─── Types ────────────────────────────────────────────────────────────────────

export interface InternacionalCardRow {
  id:                            string
  internalId:                    number
  status:                        string
  clientId:                      string | null
  clientName:                    string | null
  clientAvatarKey:               string | null
  productNames:                  string | null
  firstTripMobileNumber:         number | null
  driverNames:                   string | null
  senderName:                    string | null
  senderCityName:                string | null
  senderCountryName:             string | null
  recipientName:                 string | null
  recipientCityName:             string | null
  recipientCountryName:          string | null
  scheduledOriginArrivalAt:      string | null
  scheduledDestinationArrivalAt: string | null
  // Milestone fields
  originArrivalAt:               string | null
  originLoadingAt:               string | null
  customsOriginArrivalAt:        string | null
  borderOriginArrivalAt:         string | null
  borderDestinationArrivalAt:    string | null
  customsDestinationDepartureAt: string | null
  destinationUnloadingAt:        string | null
  finalizedAt:                   string | null
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function countryFlag(name: string | null | undefined): string {
  if (!name) return ""
  const map: Record<string, string> = {
    "Argentina":      "🇦🇷",
    "Paraguay":       "🇵🇾",
    "Uruguay":        "🇺🇾",
    "Brasil":         "🇧🇷",
    "Brazil":         "🇧🇷",
    "Chile":          "🇨🇱",
    "Bolivia":        "🇧🇴",
    "Peru":           "🇵🇪",
    "Perú":           "🇵🇪",
    "Colombia":       "🇨🇴",
    "Venezuela":      "🇻🇪",
    "Ecuador":        "🇪🇨",
    "Mexico":         "🇲🇽",
    "México":         "🇲🇽",
    "España":         "🇪🇸",
    "Estados Unidos": "🇺🇸",
    "USA":            "🇺🇸",
    "China":          "🇨🇳",
  }
  return map[name] ?? "🌐"
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—"
  const d   = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${String(d.getFullYear()).slice(-2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export function buildShareText(
  row:        InternacionalCardRow,
  milestones: Array<{ key: string; label: string }>,
): string {
  const lines: string[] = []

  lines.push(`Envío #${row.internalId} — Internacional`)
  lines.push("")

  if (row.clientName)   lines.push(`Cliente: ${row.clientName}`)
  if (row.productNames) lines.push(`Producto: ${row.productNames}`)

  const origin = [
    row.senderName,
    [row.senderCityName, row.senderCountryName].filter(Boolean).join(", "),
  ].filter(Boolean).join(" — ")

  const dest = [
    row.recipientName,
    [row.recipientCityName, row.recipientCountryName].filter(Boolean).join(", "),
  ].filter(Boolean).join(" — ")

  if (origin) lines.push(`Origen: ${origin}`)
  if (dest)   lines.push(`Destino: ${dest}`)

  if (row.scheduledOriginArrivalAt)
    lines.push(`Llegada proyectada a origen: ${fmtDate(row.scheduledOriginArrivalAt)}`)

  if (row.firstTripMobileNumber != null)
    lines.push(`Móvil: #${row.firstTripMobileNumber}`)

  const firstDriver = row.driverNames ? row.driverNames.split("||")[0]?.trim() : null
  if (firstDriver) lines.push(`Chofer: ${firstDriver}`)

  if (milestones.length > 0) {
    lines.push("")
    lines.push("--- Hitos ---")
    for (const m of milestones) {
      const val = (row as unknown as Record<string, unknown>)[m.key] as string | null | undefined
      lines.push(`${val ? "✅" : "⏳"} ${m.label}: ${val ? fmtDate(val) : "pendiente"}`)
    }
  }

  return lines.join("\n")
}

// ─── Component ────────────────────────────────────────────────────────────────

interface InternacionalShipmentCardProps {
  row:          InternacionalCardRow
  dragHandle?:  React.ReactNode
  milestones:   Array<{ key: string; label: string }>
  onView:       (id: string) => void
}

export default function InternacionalShipmentCard({
  row,
  dragHandle,
  milestones,
  onView,
}: InternacionalShipmentCardProps) {
  const [copied, setCopied] = useState(false)

  function handleCopy() {
    const text = buildShareText(row, milestones)
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const senderLabel    = row.senderName    ?? row.senderCityName    ?? null
  const recipientLabel = row.recipientName ?? row.recipientCityName ?? null
  const firstDriver    = row.driverNames   ? row.driverNames.split("||")[0]?.trim() ?? null : null

  return (
    <div className="group border rounded-xl shadow-sm hover:shadow-md transition-shadow duration-150 overflow-hidden bg-card">

      {/* ── Row 1: grip · avatar · #id · móvil a la derecha ── */}
      <div className="flex items-center gap-1.5 px-2.5 pt-2.5 pb-1.5">
        {dragHandle}

        <ClientAvatar
          clientId={row.clientId}
          clientName={row.clientName}
          className="size-6 border border-border/40"
        />

        <Link
          to={`/shipments/${row.id}`}
          className="font-mono text-[11px] font-bold text-foreground hover:text-primary hover:underline shrink-0"
        >
          #{row.internalId}
        </Link>

        <span className="ml-auto flex items-center gap-1 shrink-0">
          <TruckIcon className="size-3 text-muted-foreground" />
          <span className="text-xs font-bold tabular-nums text-foreground">
            {row.firstTripMobileNumber != null ? `#${row.firstTripMobileNumber}` : "—"}
          </span>
        </span>
      </div>

      {/* ── Row 2: chofer ── */}
      <div className="flex items-center gap-1.5 px-2.5 pb-1 min-w-0">
        <UserIcon className="size-3 text-muted-foreground shrink-0" />
        <span className="text-xs font-medium truncate text-foreground/75">
          {firstDriver ?? (
            <span className="text-muted-foreground/40 font-normal italic">Sin chofer</span>
          )}
        </span>
      </div>

      {/* ── Row 3: origen → destino con bandera ── */}
      <div className="flex flex-col gap-0 px-2.5 pb-1">
        <div className="flex items-center gap-1 min-w-0">
          <span className="size-1.5 rounded-full bg-muted-foreground/40 shrink-0" />
          {senderLabel
            ? <span className="text-xs font-bold truncate">{senderLabel}</span>
            : <span className="text-xs text-muted-foreground/40 italic">Sin origen</span>
          }
        </div>
        <div className="flex items-center gap-1 min-w-0">
          <ArrowRightIcon className="size-3 text-primary/50 shrink-0" />
          {recipientLabel
            ? <span className="text-xs font-bold truncate">{recipientLabel}</span>
            : <span className="text-xs text-muted-foreground/40 italic">Sin destino</span>
          }
          {row.recipientCountryName && (
            <span
              className="ml-auto shrink-0 text-sm leading-none"
              title={row.recipientCountryName}
            >
              {countryFlag(row.recipientCountryName)}
            </span>
          )}
        </div>
      </div>

      {/* ── Row 4: fecha scheduledOriginArrivalAt ── */}
      <div className="flex items-center gap-1 px-2.5 pb-2.5">
        <CalendarIcon className="size-3 text-muted-foreground shrink-0" />
        {row.scheduledOriginArrivalAt ? (
          <span className="text-[11px] tabular-nums text-muted-foreground">
            {fmtDate(row.scheduledOriginArrivalAt)}
          </span>
        ) : (
          <span className="text-[10px] text-muted-foreground/40">—</span>
        )}
      </div>

      {/* ── Footer (visible on hover) ── */}
      <div className="overflow-hidden transition-all duration-150 ease-out max-h-0 group-hover:max-h-16">
        <div className="flex items-center gap-1 border-t border-border/50 px-2 py-1">
          <div className="flex items-center gap-0.5 ml-auto">
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => onView(row.id)}
              title="Ver detalle"
            >
              <EyeIcon className="size-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={handleCopy}
              title={copied ? "¡Copiado!" : "Copiar datos del envío"}
              className={cn(copied && "text-emerald-600")}
            >
              <CopyIcon className="size-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
